import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useMemo, useState } from 'react';
import { FilterProps } from '../../common/filter.util';
import { Divider, Grid } from '@mui/material';
import { DeleteIcon } from '../../../../icons/Delete/DeleteIcon';
import { VentoryColor } from '../../../../util/color.util';
import { translate } from '../../../../../types/translation/Translator';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import MultiSelectionList from '../../../common/MultiSelectionList/MultiSelectionList';
import SearchBar from '../../../common/SearchBar/SearchBar';
import { StockLocationFilter } from '..';
import { StockLocationFilterMode } from '../StockLocationFilter';
import type { StockLocationFilterMode as StockLocationFilterModeType } from '../StockLocationFilter';

interface StockLocationFilterDropdownContentProps {
  filter: StockLocationFilter;
  filterProps: FilterProps;
  setFilterProps: (filter: FilterProps) => void;
  mode?: StockLocationFilterModeType;
}

export default function StockLocationFilterDropdownContent({
  filter,
  filterProps,
  setFilterProps,
  mode = StockLocationFilterMode.all,
}: StockLocationFilterDropdownContentProps) {
  const { stockLocations } = useContext(StockLocationContext);

  const [query, setQuery] = useState<string>('');

  const values = useMemo(() => {
    const items = new Set<string>();
    for (const location of stockLocations.values()) {
      if (!filter.search(location, query)) continue;
      if (filterProps.company?.size && !filterProps.company.has(location.companyId)) continue;
      items.add(location.id);
    }
    return items;
  }, [query, stockLocations, filter]);

  const getSelectedStockLocations = () => {
    switch (mode) {
      case StockLocationFilterMode.source:
        return filterProps.sourceStockLocation;
      case StockLocationFilterMode.destination:
        return filterProps.destinationStockLocation;
      case StockLocationFilterMode.all:
        return filterProps.stockLocation;
    }
  };

  const getStockLocationLabel = () => {
    switch (mode) {
      case StockLocationFilterMode.source:
        return translate({ key: TranslationKey.sourceStockLocation });
      case StockLocationFilterMode.destination:
        return translate({ key: TranslationKey.destinationStockLocation });
      case StockLocationFilterMode.all:
        return translate({ key: TranslationKey.stockLocation });
    }
  };

  const selectedStockLocations = getSelectedStockLocations();
  const stockLocationLabel = getStockLocationLabel();

  const handleChange = (selected: Set<string>) => {
    switch (mode) {
      case StockLocationFilterMode.source:
        filterProps.sourceStockLocation = selected;
        if (!filterProps.sourceStockLocation?.size) filterProps.sourceStockLocation = undefined;
        break;
      case StockLocationFilterMode.destination:
        filterProps.destinationStockLocation = selected;
        if (!filterProps.destinationStockLocation?.size) filterProps.destinationStockLocation = undefined;
        break;
      case StockLocationFilterMode.all:
        filterProps.stockLocation = selected;
        if (!filterProps.stockLocation?.size) filterProps.stockLocation = undefined;
        break;
    }

    setFilterProps({ ...filterProps });
  };

  const handleRemove = () => {
    switch (mode) {
      case StockLocationFilterMode.source:
        filterProps.sourceStockLocation = undefined;
        break;
      case StockLocationFilterMode.destination:
        filterProps.destinationStockLocation = undefined;
        break;
      case StockLocationFilterMode.all:
        filterProps.stockLocation = undefined;
        break;
    }

    setFilterProps({ ...filterProps });
  };

  return (
    <Grid container>
      <Grid item xs={12} className='flex px-3 py-2'>
        <Grid container>
          <Grid item>
            <p className='text-[13px] font-[500] text-gray-500'>{stockLocationLabel}</p>
          </Grid>
          <Grid item flexGrow={1} display={'flex'} justifyContent={'flex-end'} sx={{ color: VentoryColor.deleteRed }}>
            <Grid item onClick={handleRemove} className='cursor-pointer'>
              <DeleteIcon />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SearchBar onChange={i => setQuery(i)} className='pl-3 pr-4' />
      </Grid>
      <Grid item className='py-3' xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <MultiSelectionList
          selected={selectedStockLocations}
          values={values}
          onChange={handleChange}
          toText={id => stockLocations.get(id)?.name}
        />
      </Grid>
    </Grid>
  );
}
